import Phaser from 'phaser';
import { BaseEnemy, ALL_DIRS, Direction, GridQuery } from './BaseEnemy';
import { bfsNextStep } from '../../systems/Pathfinding';

/**
 * BFS pathfinding toward closest player, but recalcs rarely (~1.5-2s) so it
 * can be tricked. Speed 80%. Cap: 1-2 per level.
 */
export class SmartTracker extends BaseEnemy {
  private nextPathAt = 0;
  private lastDecisionCell = '';
  private cachedTarget: { col: number; row: number } | null = null;
  private getPlayerPositions: () => Array<{ x: number; y: number; alive: boolean }>;

  constructor(
    scene: Phaser.Scene,
    col: number, row: number,
    grid: GridQuery,
    getPlayerPositions: () => Array<{ x: number; y: number; alive: boolean }>,
  ) {
    super(scene, 'enemy-smarttracker', col, row, 0.8, grid);
    this.getPlayerPositions = getPlayerPositions;
  }

  update(time: number, _delta: number) {
    if (!this.alive) return;
    this.animateWalk(_delta);
    if (!this.isCenteredOnTile(6)) return;

    const me = this.centerOfTile();
    const cellKey = `${me.col},${me.row}`;
    if (cellKey === this.lastDecisionCell && this.canMoveTo(this.currentDir)) return;
    this.lastDecisionCell = cellKey;

    if (time >= this.nextPathAt) {
      this.nextPathAt = time + Phaser.Math.Between(1500, 2000);
      this.cachedTarget = this.pickTargetCell();
    }

    if (this.cachedTarget) {
      const step = bfsNextStep(this.grid, me.col, me.row, this.cachedTarget.col, this.cachedTarget.row);
      if (step) {
        const dir = this.deltaToDir(step.col - me.col, step.row - me.row);
        if (dir && this.canMoveTo(dir)) { this.applyDir(dir); return; }
      }
    }

    // Fallback: keep going or pick any open direction
    if (this.canMoveTo(this.currentDir)) { this.applyDir(this.currentDir); return; }
    const options = ALL_DIRS.filter(d => this.canMoveTo(d));
    if (options.length === 0) {
      (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
      return;
    }
    this.applyDir(Phaser.Utils.Array.GetRandom(options) as Direction);
  }

  private pickTargetCell(): { col: number; row: number } | null {
    const players = this.getPlayerPositions().filter(p => p.alive);
    if (players.length === 0) return null;
    const me = { x: this.x, y: this.y };
    const target = players.reduce((best, p) =>
      Phaser.Math.Distance.BetweenPoints(me, p) < Phaser.Math.Distance.BetweenPoints(me, best) ? p : best,
      players[0]);
    return { col: Math.floor(target.x / 48), row: Math.floor(target.y / 48) };
  }

  private deltaToDir(dc: number, dr: number): Direction | null {
    if (dc === 1 && dr === 0) return 'right';
    if (dc === -1 && dr === 0) return 'left';
    if (dc === 0 && dr === 1) return 'down';
    if (dc === 0 && dr === -1) return 'up';
    return null;
  }
}
